import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Link, useParams } from '@tanstack/react-router';
// import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { 
  ArrowLeft, 
  Edit, 
  Play, 
  Smartphone, 
  Trash2 
} from 'lucide-react';
import { mockKioskSettings } from '../data/mock-kiosk-data';
import { KioskSettings } from '../types';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export function KioskSettingsDetailsPage() {
  const { id } = useParams({ from: '/_authenticated/kiosk/settings/$id' });
  const [settings, setSettings] = useState<KioskSettings | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // In a real app, this would be an API call
    const foundSettings = mockKioskSettings.find(s => s.id === id);
    setSettings(foundSettings || null);
    setLoading(false);
  }, [id]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!settings) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Settings Not Found"
          description="The requested kiosk settings could not be found."
          actions={
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/settings">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Settings
              </Link>
            </Button>
          }
        />
        <Card>
          <CardContent className="pt-6">
            <p>The kiosk settings with ID "{id}" could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const kioskModeLabels: Record<string, string> = {
    rotation: 'Rotation',
    search: 'Search',
    browse: 'Browse'
  };
  
  return (
    <>
      <PageHeader
        title={settings.name}
        description={settings.description}
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/settings">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/devices" search={{ settingsId: settings.id }}>
                <Smartphone className="mr-2 h-4 w-4" />
                View Devices
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/preview/$id" params={{ id: settings.id }}>
                <Play className="mr-2 h-4 w-4" />
                Preview
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/settings/edit/$id" params={{ id: settings.id }}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          </>
        }
      />
      
      <div className="grid gap-4 md:grid-cols-4 mb-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge 
              className={settings.active 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
              } 
              variant="outline"
            >
              {settings.active ? 'Active' : 'Inactive'}
            </Badge>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Default Mode</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-medium">{kioskModeLabels[settings.defaultMode]}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-medium">
              {formatDistanceToNow(new Date(settings.lastUpdated * 1000), { addSuffix: true })}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Theme</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-medium capitalize">{settings.display.theme}</div>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="rotation" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rotation">Rotation</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
          <TabsTrigger value="browse">Browse</TabsTrigger>
          <TabsTrigger value="display">Display</TabsTrigger>
        </TabsList>
        
        <TabsContent value="rotation">
          <Card>
            <CardHeader>
              <CardTitle>Rotation Settings</CardTitle>
              <CardDescription>
                Configure how documents rotate in the kiosk display.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Interval</h4>
                  <p>{settings.rotation.interval} seconds</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Transition Effect</h4>
                  <p className="capitalize">{settings.rotation.transitionEffect}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Auto Start</h4>
                  <p>{settings.rotation.autoStart ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Show Metadata</h4>
                  <p>{settings.rotation.showMetadata ? 'Yes' : 'No'}</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-1">Documents ({settings.rotation.documentIds.length})</h4>
                <p className="text-sm text-muted-foreground">
                  {settings.rotation.documentIds.length > 0 
                    ? settings.rotation.documentIds.join(', ')
                    : 'No documents selected for rotation'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="search">
          <Card>
            <CardHeader>
              <CardTitle>Search Settings</CardTitle>
              <CardDescription>
                Configure search functionality for the kiosk.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Search Enabled</h4>
                  <p>{settings.search.enabled ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Autocomplete</h4>
                  <p>{settings.search.autocompleteEnabled ? 'Enabled' : 'Disabled'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Max Results</h4>
                  <p>{settings.search.maxResults}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Default Section</h4>
                  <p className="capitalize">{settings.search.defaultSection}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Fuzzy Matching</h4>
                  <p>{settings.search.fuzzyMatchingEnabled ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-1">Search Sections</h4>
                <div className="flex flex-wrap gap-2 mt-1">
                  {settings.search.searchSections.map(section => (
                    <Badge key={section} variant="outline" className="capitalize">
                      {section}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="browse">
          <Card>
            <CardHeader>
              <CardTitle>Browse Settings</CardTitle>
              <CardDescription>
                Configure browsing functionality for the kiosk.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Browse Enabled</h4>
                  <p>{settings.browse.enabled ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Categories Enabled</h4>
                  <p>{settings.browse.categoriesEnabled ? 'Yes' : 'No'}</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-1">Enabled Sections</h4>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(settings.browse.sections)
                    .filter(([_, enabled]) => enabled)
                    .map(([section]) => (
                      <Badge key={section} variant="outline" className="capitalize">
                        {section}
                      </Badge>
                    ))
                  }
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium mb-1">Grid Columns</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Mobile</p>
                    <p>{settings.browse.gridColumns.mobile}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tablet</p>
                    <p>{settings.browse.gridColumns.tablet}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Desktop</p>
                    <p>{settings.browse.gridColumns.desktop}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="display">
          <Card>
            <CardHeader>
              <CardTitle>Display Settings</CardTitle>
              <CardDescription>
                Configure display options for the kiosk interface.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Theme</h4>
                  <p className="capitalize">{settings.display.theme}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Idle Timeout</h4>
                  <p>{settings.display.idleTimeout} seconds</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Return to Home on Idle</h4>
                  <p>{settings.display.idleReturnToHome ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Show Clock</h4>
                  <p>{settings.display.showClock ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Show Logo</h4>
                  <p>{settings.display.showLogo ? 'Yes' : 'No'}</p>
                </div>
              </div>
              
              {settings.display.customCss && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Custom CSS</h4>
                  <pre className="bg-muted p-2 rounded-md text-xs overflow-auto">
                    {settings.display.customCss}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <div className="mt-6">
        <Button variant="destructive" size="sm">
          <Trash2 className="mr-2 h-4 w-4" />
          Delete Settings
        </Button>
      </div>
    </>
  );
}

export default KioskSettingsDetailsPage;
